/**
 * @file MediaLibraryModal.tsx
 *
 * Wraps Gravity's {@link MediaLibrary} modal for the admin forms' "select from library"
 * flow (product gallery, category image). On open it loads the shop's recorded media from
 * the real `/v1/webspace/media` API ({@link fetchMediaLibrary}); search, type filter, date
 * sort, and pagination run client-side over that loaded set (the list API paginates
 * newest-first only). Upload goes to the real webspace endpoint ({@link uploadImage}) and
 * delete to {@link deleteMediaAsset}. Gravity's `MediaItem` id is numeric, but a media asset
 * is a UUID, so items are displayed with a synthetic index id and translated back to their
 * real {@link LibraryMediaItem} (carrying the media-asset UUID) on select/delete. Selecting
 * items reports the chosen refs (`{ id, url, mediaType }`) and closes the modal.
 * `allowMultiSelect` toggles single vs multi selection.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  MediaLibrary,
  type FilterOption,
  type MediaItem,
} from '@group.one/react-gravity-components';
import { adminMessages } from '../i18n/messages';
import { notifyError } from '../feedback/toast';
import {
  deleteMediaAsset,
  fetchMediaLibrary,
  uploadImage,
  type LibraryMediaItem,
} from '../api/media';

const PAGE_SIZE = 12;

/** A media asset selected from the library: the media-asset UUID, its URL, and media type. */
export interface LibrarySelection {
  id: string;
  url: string;
  mediaType: string;
}

interface MediaLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (items: LibrarySelection[]) => void;
  /** Allow picking several items at once; single-select when false. Defaults to true. */
  allowMultiSelect?: boolean;
}

/** Gravity's media-type categories (its `MediaItem['mediaType']` union). */
type MediaType = 'image' | 'video' | 'pdf' | 'other';

/** Classifies a media asset for the type filter from its MIME type. */
function mediaTypeOf(mimeType: string): MediaType {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType === 'application/pdf') return 'pdf';
  return 'other';
}

/** Builds the unique `{ value, label }` filter options for a media field. */
function toFilterOptions(items: MediaItem[], pick: (item: MediaItem) => string | undefined): FilterOption[] {
  const seen = new Set<string>();
  const options: FilterOption[] = [];
  for (const item of items) {
    const value = pick(item);
    if (value && !seen.has(value)) {
      seen.add(value);
      options.push({ value, label: value });
    }
  }
  return options;
}

/**
 * Renders the media-library modal backed by the shop's `/v1` webspace media.
 *
 * @param props.isOpen - Whether the modal is shown (loads the library when it opens).
 * @param props.onClose - Closes the modal.
 * @param props.onAdd - Receives the selected media refs (media-asset id + url + type).
 * @param props.allowMultiSelect - Single vs multi selection (default true).
 *
 * @returns The media-library modal.
 */
export function MediaLibraryModal({
  isOpen,
  onClose,
  onAdd,
  allowMultiSelect = true,
}: MediaLibraryModalProps) {
  const intl = useIntl();
  const [assets, setAssets] = useState<LibraryMediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [dateSort, setDateSort] = useState<'newest' | 'oldest'>('newest');
  const [currentPage, setCurrentPage] = useState(1);

  // Loads the library (newest-first from the API); surfaces a toast on failure. Reused by
  // the open effect and the Retry affordance. Returns a canceller so a close/unmount can
  // ignore an in-flight result.
  const loadLibrary = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    void fetchMediaLibrary()
      .then((list) => {
        if (!cancelled) setAssets(list);
      })
      .catch(() => {
        if (!cancelled) {
          setAssets([]);
          notifyError(intl.formatMessage(adminMessages.toastError));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [intl]);

  // Load the library each time the modal opens.
  useEffect(() => {
    if (!isOpen) return undefined;
    return loadLibrary();
  }, [isOpen, loadLibrary]);

  // Gravity's MediaItem id is numeric; the array index is the display id. Selection is
  // resolved back to the real asset by `url` (stable across upload/delete re-indexing), so
  // no index→asset map is needed — see handleAdd.
  const items = useMemo<MediaItem[]>(
    () =>
      assets.map((asset, index) => ({
        id: index,
        filename: asset.filename,
        url: asset.url,
        thumbnailUrl: asset.url,
        mediaType: mediaTypeOf(asset.mimeType),
        mimeType: asset.mimeType,
        dateAdded: asset.createdAt,
        source: 'shop',
      })),
    [assets],
  );

  const availableSources = useMemo(() => toFilterOptions(items, (i) => i.source), [items]);
  const availableTypes = useMemo(() => toFilterOptions(items, (i) => i.mediaType), [items]);

  const filtered = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    const result = items.filter((item) => {
      if (query && !item.filename.toLowerCase().includes(query)) {
        return false;
      }
      if (selectedTypes.length > 0 && !selectedTypes.includes(item.mediaType)) {
        return false;
      }
      if (
        selectedSources.length > 0 &&
        !(item.source && selectedSources.includes(item.source))
      ) {
        return false;
      }
      return true;
    });
    result.sort((a, b) =>
      dateSort === 'newest'
        ? b.dateAdded.localeCompare(a.dateAdded)
        : a.dateAdded.localeCompare(b.dateAdded),
    );
    return result;
  }, [items, searchValue, selectedTypes, selectedSources, dateSort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const page = Math.min(currentPage, totalPages);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleAdd = useCallback(
    (selected: MediaItem[]) => {
      // Resolve by `url`, not the numeric display id: uploads prepend and deletes splice
      // `assets`, so a display id captured in Gravity's selection can point at a shifted
      // asset by the time Add fires. `url` is stable per asset.
      const refs = selected
        .map((item) => assets.find((asset) => asset.url === item.url))
        .filter((asset): asset is LibraryMediaItem => asset !== undefined)
        .map((asset) => ({ id: asset.id, url: asset.url, mediaType: mediaTypeOf(asset.mimeType) }));
      onAdd(refs);
      onClose();
    },
    [assets, onAdd, onClose],
  );

  const handleUpload = useCallback(
    (files: File[]) => {
      for (const file of files) {
        void uploadImage(file)
          .then(({ id, src }) => {
            setAssets((prev) => [
              {
                id,
                url: src,
                filename: file.name,
                mimeType: file.type || 'image/*',
                createdAt: new Date(file.lastModified).toISOString(),
              },
              ...prev,
            ]);
          })
          .catch(() => notifyError(intl.formatMessage(adminMessages.toastError)));
      }
    },
    [intl],
  );

  const handleDelete = useCallback(
    (id: number) => {
      const asset = assets[id];
      if (!asset) return;
      void deleteMediaAsset(asset.id)
        .then(() => {
          setAssets((prev) => prev.filter((a) => a.id !== asset.id));
        })
        .catch(() => notifyError(intl.formatMessage(adminMessages.toastError)));
    },
    [assets, intl],
  );

  return (
    <MediaLibrary
      isOpen={isOpen}
      items={pageItems}
      totalPages={totalPages}
      currentPage={page}
      isLoading={loading}
      searchValue={searchValue}
      selectedSources={selectedSources}
      selectedTypes={selectedTypes}
      dateSort={dateSort}
      availableSources={availableSources}
      availableTypes={availableTypes}
      allowMultiSelect={allowMultiSelect}
      onClose={onClose}
      onAdd={handleAdd}
      onSearch={(query) => {
        setSearchValue(query);
        setCurrentPage(1);
      }}
      onSourcesChange={(sources) => {
        setSelectedSources(sources);
        setCurrentPage(1);
      }}
      onTypesChange={(types) => {
        setSelectedTypes(types);
        setCurrentPage(1);
      }}
      onDateSortChange={setDateSort}
      onUpload={handleUpload}
      onDelete={handleDelete}
      onPageChange={setCurrentPage}
      onRetry={() => loadLibrary()}
      onUploadErrorDismiss={() => {}}
      labels={{ title: intl.formatMessage(adminMessages.formLibraryTitle) }}
    />
  );
}
